import { plural } from '@tabel/shared/sites/step-texts';

/*
 * Тексты страниц «Настройки → Площадки» (007, T039–T041, T047, T053, T055), которых нет в
 * `@tabel/shared/sites/step-texts`: список площадок, окно шага 1, шапка страницы площадки, формы шагов, «Что случилось»
 * (тревоги), «Ремонт», «Опасная зона», раздел «Тревоги площадок». Тексты шагов, строки состояния, подсказки и
 * действия-ссылки шагов — только из `step-texts`. Источник слов — `prototypes/site-onboarding.html` и
 * `site-onboarding-design.md` (разделы 1, 2, 6, «Опасная зона»), spec.md FR-002, FR-016, FR-027, FR-041, FR-050…FR-052,
 * FR-061, FR-062. Тексты — ASD-STE100: короткие предложения, нейтральный род, названия программ и полей — как в
 * SmartPSS, Windows, WireGuard и ntfy. Проверка — `texts.test.tsx`.
 */

// ── Числа ──

const NUM = new Intl.NumberFormat('ru-RU');
/** Число с разрядами: «2 840». */
export const num = (n: number) => NUM.format(n);
const PASSES = ['отметка', 'отметки', 'отметок'] as const;
const PEOPLE = ['человек', 'человека', 'человек'] as const;
const TIMESHEETS = ['табель', 'табеля', 'табелей'] as const;
const ROWS_ACC = ['строку', 'строки', 'строк'] as const;
const ROWS_GEN = ['строки', 'строк', 'строк'] as const;
const RECORDS = ['запись', 'записи', 'записей'] as const;
const TIMES = ['раз', 'раза', 'раз'] as const;
/** «1 204 отметки». */
const count = (n: number, forms: readonly [string, string, string]) => `${num(n)} ${plural(n, forms)}`;

/** Раздел «Площадки» (FR-002, `site-onboarding-design.md` раздел 2). */
export const LIST = {
  title: 'Площадки',
  description: 'Откуда в сервис приходят отметки: настройка для всех пользователей.',
  add: 'Подключить площадку',
  columns: {
    site: 'Площадка',
    city: 'Часовой пояс',
    terminals: 'Терминалы',
    last: 'Последняя отметка',
    state: 'Состояние',
  },
  /** Подписи значений в карточке строки на телефоне (`t-tbl-stack`): шапки таблицы там нет. */
  phone: { terminals: 'Терминалов:', last: 'Последняя отметка:' },
  emptyTitle: 'Площадок пока нет',
  emptyText: 'Здесь появятся площадки и их состояние. Нажмите «Подключить площадку».',
  loadError: 'Список площадок не загрузился',
  forbiddenTitle: 'Нет доступа',
  forbidden: 'Площадки видит только администратор системы.',
} as const;

/** Окно «Подключить площадку» — шаг 1 (FR-017; «Что понадобится» — `site-onboarding-design.md` раздел 1). */
export const CREATE = {
  title: 'Подключить площадку',
  /** Окно защиты несохранённого (FR-008): что не сохранено. */
  unsaved: (name: string) => (name ? `Площадка «${name}»` : 'Новая площадка'),
  description: 'Сервис подготовит место для площадки на сервере. Потом страница площадки проведёт по шагам.',
  needTitle: 'Что понадобится',
  /** Пункты списка — не предложения: без точки в конце. */
  need: [
    'Доступ к компьютеру со SmartPSS на площадке — на месте или удалённо',
    'Права администратора Windows на этом компьютере — только для VPN',
    'Вход в SmartPSS с правами администратора',
    'Человек на площадке на 15 минут — для контрольного прохода, по желанию',
    'Около часа времени. Потом сервис 3 дня наблюдает сам',
  ],
  namePlaceholder: 'например, Северная',
  cityPlaceholder: 'Выберите город с таким же временем',
  cityHint: 'Город здесь задаёт только часовой пояс. Выберите любой с таким же местным временем.',
  otherCityPlaceholder: 'например, Сургут',
  tzPlaceholder: 'Выберите пояс',
  codeLabel: 'Код площадки',
  codeHint:
    'Код может содержать латиницу, цифры и «-», от 2 до 20 знаков. Код виден в адресе страницы площадки.',
  submit: 'Подключить площадку',
  cancel: 'Отмена',
  errors: {
    name: 'Введите название площадки',
    city: 'Выберите часовой пояс по городу с таким же временем',
    otherCity: 'Введите город с таким же временем',
    tz: 'Выберите часовой пояс',
    code: 'Код: латиница, цифры, «-», 2–20 знаков',
  },
} as const;

/** Страница площадки (FR-010, FR-016; `site-onboarding-design.md` раздел 2). */
export const SITE = {
  description: 'Подключение площадки для всех пользователей: каждый шаг сервис проверяет сам.',
  cityChip: 'Часовой пояс',
  startedChip: 'Начато',
  stepsLabel: 'Шаги подключения',
  loading: 'Загрузка…',
  loadError: 'Страница площадки не загрузилась',
  forbiddenTitle: 'Нет доступа',
  forbidden: 'Страницу площадки видит только администратор системы этой площадки.',
  notFoundTitle: 'Площадка не найдена',
  notFound: 'Площадки с таким адресом нет. Откройте её из списка площадок.',
  toList: 'К списку площадок',
  retry: 'Повторить',
  errorCode: 'Код ошибки:',
  /** Пароль скрыт: столько же знаков при любой длине. */
  mask: '●●●●●●●●●●●●',
  external: 'откроется в новой вкладке',
  copyFailed: 'Не удалось скопировать. Текст выделен: скопируйте его сами.',
  addressAllowed: (address: string) => `Адрес ${address} разрешён`,
  launched: (name: string) => `Площадка «${name}» подключена к табелям`,
} as const;

/** Формы шагов: кнопки и ошибки ввода, которых нет в `STEP_BODY`. */
export const FORM = {
  saveAnswers: 'Сохранить ответы',
  answersSaved: 'Ответы сохранены',
  /** Окно защиты несохранённого (FR-008): что не сохранено. */
  answersUnsaved: (title: string) => `Ответы шага 2 «${title}»`,
  required: 'Ответьте на вопрос',
  terminals: 'Введите число от 1 до 100',
  keepDays: 'Введите число дней от 0 до 3650',
  direction: 'Выберите направление',
  startDate: 'Выберите дату начала табелей',
  rulesSource: 'Выберите площадку с правилами',
  rulesSourceError: 'Образцы правил не загрузились',
  rulesSourceEmpty:
    'Нет работающей площадки с действующими правилами. Попросите администратора правил другой площадки опубликовать её правила.',
  /** Шаг 8: итог загрузки (`SiteUpload`) тостом. */
  uploaded: (fileName: string) => `Файл загружен: ${fileName}`,
  /** Шаг 9: пункт наблюдения выполнен или ещё нет — словами рядом со значком. */
  itemDone: 'готово',
  itemWait: 'ещё нет',
  itemBad: 'нужно исправить',
} as const;

/** «Опасная зона» страницы площадки (T055; FR-061, FR-062, US7; ux.md §11 «Опасные действия»). */
export const DANGER = {
  title: 'Опасная зона',
  password: {
    title: 'Выдать новый пароль',
    text: 'Старый пароль SmartPSS перестанет работать сразу. Новый пароль нужно будет ввести в SmartPSS.',
    confirmTitle: (name: string) => `Выдать новый пароль площадке «${name}»?`,
    confirmText:
      'Старый пароль перестанет работать сразу. Шаг 4 откроется с новым паролем: введите его в SmartPSS.',
    done: 'Новый пароль выдан. Введите его в SmartPSS',
  },
  vpnKey: {
    title: 'Выдать новый файл',
    text: 'Старый файл подключения перестанет работать сразу. Новый файл нужно будет загрузить в WireGuard.',
    confirmTitle: (name: string) => `Выдать новый файл подключения площадке «${name}»?`,
    confirmText:
      'Старый файл перестанет работать сразу. Шаг 3 откроется с новым файлом: загрузите его в WireGuard.',
    done: 'Новый файл подключения выдан. Скачайте его в шаге 3',
  },
  link: {
    title: 'Сменить способ связи',
    text: 'Площадка вернётся к шагам 3 и 4. Остальные шаги останутся выполненными.',
    confirmTitle: (mode: string) => `Сменить способ связи на «${mode}»?`,
    confirmText: 'Площадка вернётся к шагам 3 и 4. Остальные шаги останутся выполненными.',
    to: (mode: string) => `Сменить на «${mode}»`,
  },
  disable: {
    title: 'Отключить площадку',
    text: 'Соединение и вход SmartPSS перестанут работать. Отметки и табели останутся.',
    confirmTitle: (name: string) => `Отключить площадку «${name}»?`,
    confirmText: 'Соединение и вход SmartPSS перестанут работать сразу. Отметки, люди и табели останутся.',
    fieldLabel: 'Введите название площадки',
    /** Начало текста окна, когда числа есть: дальше — «Останутся: …». */
    confirmStop: 'Соединение и вход SmartPSS перестанут работать сразу.',
    /** Что останется (`GET …/consequences`, FR-062): «Останутся: 1 204 отметки, 38 человек, 2 табеля.» */
    consequences: (passes: number, people: number, timesheets: number) =>
      `Останутся: ${count(passes, PASSES)}, ${count(people, PEOPLE)}, ${count(timesheets, TIMESHEETS)}.`,
    consequencesLoading: 'Считаем, сколько данных останется…',
    consequencesFailed: 'Числа сейчас не загрузились.',
    done: (name: string) => `Площадка «${name}» отключена`,
    undo: 'Отменить',
  },
  enable: {
    title: 'Площадка отключена',
    text: 'Соединение и вход SmartPSS не работают. Отметки, люди и табели остались.',
    button: 'Подключить снова',
    done: (name: string) => `Площадка «${name}» снова подключена`,
  },
  /**
   * Прежняя учётная запись первой площадки (T049; spec.md US5, research R17, S103; `SiteDetail.legacy`). Причины —
   * по коду `legacy.reason`.
   */
  legacy: {
    title: 'Отключить прежнюю учётную запись',
    text: 'SmartPSS площадки перестанет входить под прежним именем. Отметки будут идти только через новую учётную запись.',
    confirmTitle: (name: string) => `Отключить прежнюю учётную запись площадки «${name}»?`,
    confirmText:
      'Прежняя учётная запись SmartPSS перестанет работать сразу. Отметки, люди и табели останутся.',
    done: 'Прежняя учётная запись отключается',
    reasons: {
      no_session: 'Когда SmartPSS войдёт под новой учётной записью, кнопка станет доступна.',
      observing: (date: string) =>
        `Кнопка станет доступна ${date}: до этой даты идёт наблюдение за новой учётной записью.`,
      no_address: 'Когда сервис узнает адрес компьютера площадки, кнопка станет доступна.',
      pending: 'Сервис уже отключает прежнюю учётную запись.',
      retired: 'Прежняя учётная запись уже отключена.',
    },
  },
} as const;

/**
 * «Что случилось» — открытые тревоги площадки (T047; spec.md FR-050, FR-051, US4; site-onboarding-design.md раздел 6).
 * Слова — как в тревоге ntfy (`api/src/modules/sites/alerts-eval.ts`, `alertText`). Детали — числа, время и адреса из
 * ответа сервера. Действия-ссылки шагов — из `step-texts` (`ACTIONS`), остальные — здесь.
 */
export const ALERTS = {
  title: 'Что случилось',
  since: (when: string) => `Тревога с ${when}`,
  showRows: 'Показать строки',
  upload: 'Загрузить выгрузку',
  kinds: {
    pc_offline: {
      title: 'Компьютер площадки не на связи',
      text: 'Проверьте, что компьютер включён, а в WireGuard нажато «Подключить».',
      last: (when: string) => `Последний сигнал VPN был ${when}.`,
    },
    smartpss_silent: {
      title: 'SmartPSS не передаёт отметки',
      text: 'В рабочее время отметок нет дольше обычного. Проверьте, что SmartPSS работает и база данных в нём включена.',
      last: (when: string) => `Последняя отметка пришла ${when}.`,
    },
    extract_missed: {
      title: 'Выгрузка пока не подтверждена',
      text: 'Без новых отметок нельзя определить, сработало ли расписание. В SmartPSS проверьте «Системные настройки» → «Управление данными» и дождитесь следующей выгрузки.',
      at: (time: string) => `Выгрузка была запланирована на ${time}.`,
    },
    new_address: {
      title: 'Новый адрес площадки',
      from: (from: string, allowed: string) =>
        `SmartPSS пришёл с адреса ${from}, а площадке разрешён ${allowed}.`,
      fromOnly: (from: string) => `SmartPSS пришёл с нового адреса ${from}.`,
      text: 'Если адрес площадки сменился, разрешите новый адрес.',
    },
    auth_failures: {
      title: 'Много неудачных входов',
      count: (n: number) =>
        `За последний час под учётной записью площадки не удалось войти ${count(n, TIMES)}.`,
      text: 'Если это не SmartPSS площадки, выдайте новый пароль.',
    },
    unknown_codes: {
      title: 'Незнакомые коды в отметках',
      text: 'SmartPSS прислал отметки с кодами, которых сервис не знает. Эти отметки ждут проверки в разделе «Ремонт».',
    },
    schema_changed: {
      title: 'SmartPSS прислал данные в другом формате',
      text: 'Сервис не знает этот формат. Новые отметки ждут проверки в разделе «Ремонт».',
    },
    deletes: {
      title: 'В SmartPSS удалили записи',
      today: (n: number) => `Сегодня удалено ${count(n, RECORDS)}.`,
      text: 'В табеле эти записи остались. Проверьте, кто их удалил.',
    },
    clock_skew: {
      title: 'Часы терминала расходятся с сервером',
    },
    quarantine: {
      title: 'Отметки ждут проверки',
      rows: (n: number) => `Сервис не стал считать ${count(n, ROWS_ACC)}: они пришли не так, как обычно.`,
      text: 'Проверьте их в разделе «Ремонт» и выпустите в обработку.',
    },
  },
} as const;

/**
 * «Ремонт» страницы площадки и загрузка выгрузки записей в шаге 10 (T047; spec.md FR-041, US4 сценарий 4; data-model
 * А.6, А.10): подсказка периода, предпросмотр → «Добавить», итог, история, строки на проверке (карантин).
 */
export const REPAIR = {
  title: 'Ремонт',
  note: 'Пропущенные отметки можно вернуть из SmartPSS. Сервис добавит только недостающие отметки, без повторов.',
  uploadTitle: 'Выгрузка записей',
  hintTitle: 'Отметки за эти часы могли не дойти.',
  hintText: 'Загрузите выгрузку записей за эти дни.',
  /** Период подсказки: «20.10.2026 10:00 – 20.10.2026 14:30». */
  period: (from: string, to: string) => `${from} – ${to}`,
  checking: 'Проверяем файл…',
  adding: 'Добавляем отметки…',
  /** Предел файла — contracts/http-api.md, «Загрузки выгрузок SmartPSS». */
  tooLarge: 'Файл весит больше 20 МБ. Выгрузите период короче.',
  preview: {
    title: (file: string) => `Файл «${file}» проверен.`,
    rows: (recognized: number, total: number) =>
      `Распознано ${num(recognized)} из ${count(total, ROWS_GEN)}.`,
    rejected: (n: number) => `Отклонено ${count(n, ROWS_ACC)}.`,
    period: (from: string, to: string) => `Файл содержит отметки с ${from} по ${to}.`,
    columns: (list: string) => `Сервис нашёл колонки: ${list}.`,
    column: (name: string, header: string) => `${name} — «${header}»`,
    nothing: 'В файле нет строк, которые можно добавить.',
    note: 'Повторы сервис пропустит сам.',
    add: 'Добавить',
    cancel: 'Отмена',
  },
  columnNames: {
    person: 'номер человека',
    name: 'имя',
    time: 'время',
    date: 'дата',
    device: 'терминал',
    state: 'направление',
  },
  result: {
    title: (file: string) => `Файл «${file}» загружен.`,
    text: (added: number, duplicates: number, rejected: number) =>
      `Добавлено ${count(added, PASSES)}, повторов ${num(duplicates)}, отклонено ${num(rejected)}.`,
  },
  /** Отклонённые строки — номерами и причиной, без ФИО (data-model А.6). */
  rejectedReasons: {
    no_person: 'нет номера человека',
    no_name: 'нет имени',
    bad_time: 'время не распознано',
    no_device: 'нет терминала',
  },
  /** Пункт списка, не предложение: без точки в конце. */
  rejectedRow: (rows: string, reason: string) => `Строка ${rows}: ${reason}`,
  rejectedRows: (rows: string, reason: string) => `Строки ${rows}: ${reason}`,
  rejectedCount: (n: number, reason: string) => `Отклонено ${count(n, ROWS_ACC)}: ${reason}`,
  more: (n: number) => `ещё ${num(n)}`,
  historyTitle: 'Загрузки записей',
  historyEmpty: 'Загрузок записей пока не было.',
  hintError: 'Подсказка периода не загрузилась',
  /** Причина периода ремонта (`RepairReason`): часть строки после времени, со строчной буквы. */
  reasons: {
    pc_offline: 'компьютер площадки был не на связи',
    smartpss_silent: 'SmartPSS не передавал отметки',
    extract_missed: 'выгрузка по расписанию не подтверждена',
    ingest_error: 'сервис не принимал отметки',
  },
  quarantine: {
    title: 'Строки на проверке',
    note: 'Новые строки ждут проверки. Проверьте причину и выпустите строки в обработку. Если обработка не завершилась, повторите её.',
    empty: 'Строк на проверке нет.',
    loadError: 'Строки на проверке не загрузились',
    columns: { row: 'Строка копии', reason: 'Причина', seen: 'Пришла', state: 'Состояние' },
    reasons: {
      unknown_site: 'Пришла от неизвестной площадки',
      src_mismatch: 'Пришла под учётной записью другой площадки',
      unknown_code: 'Незнакомый код в отметке',
      new_columns: 'SmartPSS прислал данные в другом формате',
    },
    waiting: 'Ждёт проверки',
    released: (when: string) => `Выпущена ${when}`,
    selectAll: 'Выбрать все строки',
    select: (n: number) => `Выбрать строку копии ${n}`,
    release: (n: number) => `Выпустить ${count(n, ROWS_ACC)}`,
    confirmTitle: (n: number) => `Выпустить ${count(n, ROWS_ACC)} в обработку?`,
    confirmText: 'Сервис посчитает эти строки в табеле.',
    reasonLabel: 'Причина',
    reasonHint: 'Напишите причину: от 5 до 500 знаков. Она попадёт в журнал действий.',
    submit: 'Выпустить строки',
    retry: 'Повторить обработку',
    retryRow: (id: number) => `Повторить обработку строки ${id}`,
    retryTitle: (n: number) => `Повторить обработку ${count(n, ROWS_GEN)}?`,
    done: (n: number, imported: number) => `Строки выпущены: ${num(n)}. Обработано строк: ${num(imported)}.`,
  },
} as const;

/**
 * Раздел «Тревоги площадок» (T047; spec.md FR-052, «Права ролей»; data-model А.9): кому сообщать о тревогах каждой
 * площадки — тема ntfy (сообщение на телефон через наш сервер уведомлений) или пользователь сервиса.
 */
export const RECIPIENTS = {
  title: 'Тревоги площадок',
  description:
    'Сервис сообщает о тревогах площадки получателям из этого списка. Настройка действует для всех пользователей.',
  /**
   * Способ в строке списка. «Уведомление в сервисе» добавить нельзя (решение координатора до страницы «Пользователи и
   * роли»), но заданного раньше получателя список показывает и сохраняет.
   */
  channels: { ntfy: 'Сообщение на телефон', inapp: 'Уведомление в сервисе' },
  targetLabel: 'Тема ntfy',
  targetHint:
    'Сообщение придёт на телефон через наш сервер уведомлений. Подпишитесь на эту тему в программе ntfy.',
  placeholder: (example: string) => `например, ${example}`,
  user: (target: string) => `Пользователь ${target}`,
  add: 'Добавить получателя',
  remove: (who: string) => `Убрать получателя ${who}`,
  added: 'Получатель добавлен',
  removed: 'Получатель убран',
  undo: 'Отменить',
  listLabel: (site: string) => `Получатели тревог площадки «${site}»`,
  empty: 'Получателей нет. Тревоги этой площадки показаны только на её странице.',
  errors: {
    ntfy: 'Тема: латиница, цифры, «-» и «_», до 64 знаков',
    duplicate: 'Такой получатель уже есть',
    max: 'Получателей у площадки не больше 50',
  },
  emptyTitle: 'Площадок пока нет',
  emptyText: 'Здесь появятся площадки и получатели их тревог. Подключите площадку в разделе «Площадки».',
  sitesError: 'Список площадок не загрузился',
  loadError: 'Получатели тревог не загрузились',
  forbiddenTitle: 'Нет доступа',
  forbidden: 'Получателей тревог задаёт только администратор системы площадки.',
  toSite: 'Открыть площадку',
} as const;

/** Примеры значений полей — данные, а не слова: проверка языка их не смотрит. */
export const EXAMPLES = { ntfyTopic: 'efes-sev' } as const;
